import CampaignIcon from "@mui/icons-material/Campaign";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LinkIcon from "@mui/icons-material/Link";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Store";
import TuneIcon from '@mui/icons-material/Tune';
import TableChartIcon from '@mui/icons-material/TableChart';
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { Box, IconButton, List } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logo.svg?react";
import useTimeoutedEffect from "../../hooks/useTimeoutEffect";
import { apiGetBrandsList } from "../../services/brand.api";
import { apiIsEnrichmentActive } from "../../services/enrichment.api";
import { useAuthStore } from "../../store/useAuthStore";
import { Brand } from "../../types/brand";
import { useCan, useScope, billing, Can } from "../../auth";
import BrandSidebarItem from "./BrandSidebarItem";
import SidebarItem, { SidebarItemData } from "./SidebarItem";
import SmallSidebarItem from "./SmallSidebarItem";

interface SidebarProps {
  expanded: boolean;
  onToggle: () => void;
}

export type BrandSelectHandler = (
  brandId: string | null,
  showChildrenStatistics?: boolean,
) => void

const ROOT_ADMIN_ROUTES = ["/brands"];
const BRAND_ADMIN_ROUTES = ["/brand-config"];
const COMMON_ROUTES = [
  "/dashboard",
  "/connections",
  "/campaigns",
  "/data-enrichment",
];


const MENU_ITEMS: SidebarItemData[] = [
  {
    label: "Dashboard",
    route: "/dashboard",
    icon: <DashboardIcon />,
  },
  {
    label: "Connections",
    route: "/connections",
    icon: <LinkIcon />,
  },
  {
    label: "Brands",
    route: "/brands",
    icon: <StoreIcon />,
  },
  {
    label: "Campaigns",
    route: "/campaigns",
    icon: <CampaignIcon />,
  },
  {
    label: "Data Enrichment",
    route: "/data-enrichment",
    icon: <TableChartIcon />,
  },
  {
    label: "Brand Configuration",
    route: "/brand-config",
    icon: <TuneIcon />,
  },
];



export type SidebarBrand = Pick<
  Brand,
  'name' |
  'domain' |
  'logo_url' |
  'children'
> & {
  id: string | null
}

export interface SelectedBrand {
  brandId: string | null,
  showChildrenStatistics?: boolean,
}

const GLOBAL_BRAND: SidebarBrand = {
  id: null,
  name: "All Brands",
  domain: "",
  logo_url: null,
  children: []
}


const Sidebar: React.FC<SidebarProps> = ({ expanded, onToggle }) => {
  const [adminBrand, setAdminBrand] = useState<SidebarBrand | null>(null);
  const [childBrands, setChildBrands] = useState<SidebarBrand[] | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<SelectedBrand>({ brandId: null });
  const [enrichmentActive, setEnrichmentActive] = useState(false);

  const nav = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();
  const can = useCan();
  const { isBrandScoped, isGlobalScope } = useScope();
  const isDashboardRoute = location.pathname.includes('/dashboard');

  // Get admin brand from auth store (fetched on login)
  const { userAdminBrand } = useAuthStore();

  // Check if enrichment service is active for brand admins
  useEffect(() => {
    if (isGlobalScope) {
      setEnrichmentActive(true); // Global/root scope always sees enrichment
      return;
    }
    if (isBrandScoped && userAdminBrand?.id) {
      apiIsEnrichmentActive(userAdminBrand.id).then(setEnrichmentActive);
    }
  }, [isGlobalScope, isBrandScoped, userAdminBrand?.id]);

  useTimeoutedEffect(async () => {
    const brands = await apiGetBrandsList();
    if (!brands || !brands.length) return;

    const newAdminBrand = isGlobalScope ? GLOBAL_BRAND : brands[0]
    setAdminBrand(newAdminBrand);
    setChildBrands(isGlobalScope ? brands : (brands[0]?.children || []));
    setSelectedBrand({
      brandId: newAdminBrand.id,
      showChildrenStatistics: !!newAdminBrand.children.length
    })


  }, [isGlobalScope]);

  // Handle brand selection from sidebar
  const handleBrandSelect: BrandSelectHandler = (
    brandId: string | null,
    showChildrenStatistics: boolean = false,
  ) => {
    setSelectedBrand({ brandId, showChildrenStatistics });
    nav(`/dashboard${brandId ? `/${brandId}?showChildrenStatistics=${showChildrenStatistics}` : ''}`);
  };

  // Filter menu items based on user role and service availability
  const menuItems = MENU_ITEMS.filter((item) => {
    // Hide Data Enrichment if service not active
    if (item.route === "/data-enrichment" && !enrichmentActive) return false;
    return [
      ...COMMON_ROUTES,
      ...(isGlobalScope ? ROOT_ADMIN_ROUTES : []),
      ...(isBrandScoped ? BRAND_ADMIN_ROUTES : []),
    ].includes(item.route || "");
  });

  return (
    <Box
      sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        width: expanded ? "280px" : "96px",
        bgcolor: "#FFFFFF",
        borderRight: "1px solid #E5E7EB",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        zIndex: 50,
        transition: "width 0.2s ease-in-out",
        overflow: "hidden",
      }}
    >
      {/* Logo + Expand/Collapse Button */}
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {expanded ? (
          <>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {userAdminBrand?.logo_url ? (
                <Box
                  component="img"
                  src={userAdminBrand.logo_url}
                  alt="Brand logo"
                  sx={{ height: "32px", width: "auto", maxWidth: "200px", objectFit: "contain" }}
                />
              ) : (
                <Logo style={{ height: "32px", width: "auto" }} />
              )}
            </Box>
            <IconButton
              onClick={onToggle}
              sx={{
                p: 1.5,
                "&:hover": { bgcolor: "#F5F7FA" },
                ml: 1,
                mr: 1,
              }}
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon sx={{ width: 20, height: 20, color: "#5C6B80" }} />
            </IconButton>
          </>
        ) : (
          <>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ fontSize: "24px", fontWeight: 500, color: "#0B1620" }}>
                {userAdminBrand?.name ? userAdminBrand.name.charAt(0).toUpperCase() : 'T'}
              </Box>
            </Box>
            <IconButton
              onClick={onToggle}
              sx={{
                p: 1.5,
                "&:hover": { bgcolor: "#F5F7FA" },
              }}
              aria-label="Expand sidebar"
            >
              <ChevronRightIcon sx={{ width: 20, height: 20, color: "#5C6B80" }} />
            </IconButton>
          </>
        )}
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 2 }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => {
            const { label, icon, route } = item;
            const isSelected = location.pathname.includes(route || '');
            const isDashboard = route === "/dashboard";
            const showBrandItems = isDashboardRoute && isDashboard && adminBrand;

            // For brand admins and root admins, clicking Dashboard also sets rollup mode
            const handleClick = () => {
              if (!route) return;
              if (isDashboard && (isBrandScoped || isGlobalScope)) {
                setSelectedBrand({ brandId: null }); // Set rollup mode
              }
              nav(route);
            };

            return (
              <React.Fragment key={label}>
                <SidebarItem
                  label={label || ""}
                  icon={icon}
                  selected={isSelected}
                  expanded={expanded}
                  onClick={handleClick}
                />
                {/* Brand hierarchy for brand admins - only visible when Dashboard is selected */}
                {showBrandItems && (
                  <BrandSidebarItem
                    brand={adminBrand}
                    childBrands={childBrands || []}
                    selectedBrand={selectedBrand}
                    expanded={expanded}
                    onBrandSelect={handleBrandSelect}
                  />
                )}
              </React.Fragment>
            );
          })}
        </List>

        <Box sx={{ px: 3, py: 2 }}>
          <Box sx={{ width: "100%", height: "1px", bgcolor: "#E5E7EB" }} />
        </Box>

        <List sx={{ p: 0 }}>
          <Can perm={billing.read.GetBillingData}>
            <SmallSidebarItem
              label="Billing"
              icon={<CreditCardIcon />}
              route="/billing"
              expanded={expanded}
            />
          </Can>
          <SmallSidebarItem
            label="Set up your Trust ID"
            icon={<VerifiedUserIcon />}
            route="/trustid-setup"
            expanded={expanded}
          />
          <SmallSidebarItem
            label="Account Settings"
            icon={<SettingsIcon />}
            route="/account"
            expanded={expanded}
          />
        </List>
      </Box>
      <Box sx={{ px: 3, pb: 3 }}>
        <List sx={{ p: 0 }}>
          <SmallSidebarItem
            label="Logout"
            icon={<LogoutIcon />}
            route="/logout"
            expanded={expanded}
            onClose={logout}
          />
        </List>
      </Box>
    </Box>
  );
};

export default Sidebar;
